/** QuickPlan — Composant racine : accueil projets, puis atelier (bibliothèque / structure,
 *  vue 3D, propriétés / assistant), nomenclature ou notice de montage. Raccourcis clavier globaux. */
import { useEffect } from 'react';
import { useProjectStore } from './store/useProjectStore.js';
import ProjectPicker from './components/ProjectPicker.jsx';
import LibraryPanel from './components/LibraryPanel.jsx';
import StructurePanel from './components/StructurePanel.jsx';
import PropsPanel from './components/PropsPanel.jsx';
import ChatPanel from './components/ChatPanel.jsx';
import BomView from './components/BomView.jsx';
import NoticeView from './components/NoticeView.jsx';
import TemplateDialog from './components/TemplateDialog.jsx';
import StatusBar from './components/StatusBar.jsx';
import Viewer3D from './viewer3d/Viewer3D.jsx';

function useShortcuts() {
  useEffect(() => {
    const onKey = (e) => {
      const st = useProjectStore.getState();
      if (!st.project || st.mode !== 'design' || st.dialog) return;
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      const k = e.key.toLowerCase();
      if ((e.ctrlKey || e.metaKey) && k === 'z') { e.preventDefault(); e.shiftKey ? st.redo() : st.undo(); return; }
      if ((e.ctrlKey || e.metaKey) && k === 'y') { e.preventDefault(); st.redo(); return; }
      if ((e.ctrlKey || e.metaKey) && k === 'd') { e.preventDefault(); st.duplicateSelection(); return; }
      if ((e.ctrlKey || e.metaKey) && k === 'a') { e.preventDefault(); st.setSelection(st.project.instances.map((i) => i.id)); return; }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === 'Delete' || e.key === 'Backspace') st.deleteSelection();
      else if (e.key === 'Escape') { if (st.tool !== 'select') st.setTool('select'); else st.setSelection([]); }
      else if (k === 'g') { st.setTool('select'); st.setGizmo('translate'); }
      else if (k === 'r') { st.setTool('select'); st.setGizmo('rotate'); }
      else if (k === 'h' && e.shiftKey) st.showAll();
      else if (k === 'h') st.hideSelection();
      else if (k === 'b') st.toggle('boxMode');
      else if (k === 'o') st.selection.forEach((id) => st.toggleOpen(id));
      else if (k === 'f') st.fit();
      else if (k === 'p') st.toggle('showPorts');
      else if (k === '1') st.setView('persp');
      else if (k === '2') st.setView('front');
      else if (k === '3') st.setView('top');
      else if (k === '4') st.setView('right');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}

export default function App() {
  const project = useProjectStore((s) => s.project);
  const mode = useProjectStore((s) => s.mode);
  const setMode = useProjectStore((s) => s.setMode);
  const leftTab = useProjectStore((s) => s.leftTab);
  const setLeftTab = useProjectStore((s) => s.setLeftTab);
  const rightTab = useProjectStore((s) => s.rightTab);
  const setRightTab = useProjectStore((s) => s.setRightTab);
  const closeProject = useProjectStore((s) => s.closeProject);
  const saveState = useProjectStore((s) => s.saveState);
  const undo = useProjectStore((s) => s.undo);
  const redo = useProjectStore((s) => s.redo);
  const canUndo = useProjectStore((s) => s.past.length > 0);
  const canRedo = useProjectStore((s) => s.future.length > 0);
  const aiJob = useProjectStore((s) => s.ai.job);
  const dialog = useProjectStore((s) => s.dialog);
  useShortcuts();

  if (!project) return <ProjectPicker />;

  const saveLabel = { saving: '💾 Sauvegarde…', saved: '✓ Sauvegardé', error: '⚠ Erreur de sauvegarde', paused: '⏸ Assistant en cours' }[saveState] ?? '';

  return (
    <div className="app">
      <div className="topbar">
        <h1>Quick<span className="logo-sfx">·Plan</span></h1>
        <button onClick={closeProject} title="Retour aux projets">←</button>
        <span className="project-name">{project.name}</span>
        {project.role === 'viewer'
          ? <span className="ro-badge" title="Projet partagé en lecture seule">👁 Lecture seule</span>
          : <span className={`save-state ${saveState === 'error' ? 'error' : ''}`}>{saveLabel}</span>}
        <div className="seg">
          <button disabled={!canUndo || !!aiJob} onClick={undo} title="Annuler (Ctrl+Z)">↶</button>
          <button disabled={!canRedo || !!aiJob} onClick={redo} title="Refaire (Ctrl+Y)">↷</button>
        </div>
        <span className="spacer" />
        {aiJob && <span className="ai-badge"><span className="spinner" /> L'assistant modifie le projet</span>}
        <div className="tabs">
          <button className={mode === 'design' ? 'active' : ''} onClick={() => setMode('design')}>Conception 3D</button>
          <button className={mode === 'bom' ? 'active' : ''} onClick={() => setMode('bom')}>Nomenclature</button>
          <button className={mode === 'notice' ? 'active' : ''} onClick={() => setMode('notice')}>Notice de montage</button>
        </div>
      </div>

      {mode === 'bom' ? (
        <div className="main scroll"><BomView /></div>
      ) : mode === 'notice' ? (
        <div className="main scroll"><NoticeView /></div>
      ) : (
        <div className="main">
          <aside className="side left">
            <div className="side-tabs">
              <button className={leftTab === 'library' ? 'active' : ''} onClick={() => setLeftTab('library')}>Bibliothèque</button>
              <button className={leftTab === 'structure' ? 'active' : ''} onClick={() => setLeftTab('structure')}>Structure</button>
            </div>
            {leftTab === 'library' ? <LibraryPanel /> : <StructurePanel />}
          </aside>
          <div className="canvas-area"><Viewer3D /></div>
          <aside className="side right">
            <div className="side-tabs">
              <button className={rightTab === 'props' ? 'active' : ''} onClick={() => setRightTab('props')}>Propriétés</button>
              <button className={rightTab === 'chat' ? 'active' : ''} onClick={() => setRightTab('chat')}>Assistant{aiJob ? ' ●' : ''}</button>
            </div>
            {rightTab === 'props' ? <div className="panel-body"><PropsPanel /></div> : <ChatPanel />}
          </aside>
        </div>
      )}
      <StatusBar />
      {dialog?.kind === 'template' && <TemplateDialog template={dialog.template} />}
    </div>
  );
}
